import React from 'react'
import parse, { domToReact, HTMLReactParserOptions } from 'html-react-parser'
import { WP_REST_API_Post } from 'wp-types'
import Separator from '../../components/Separator'
import { Color } from '../../utils/constants'
import Tag from '../../components/Tag'
import Category from '../../components/Category'
import { twMerge } from 'tailwind-merge'
import { getCategoryAndTags } from './utils'
import { Link } from '../../utils/helpers'
import Image from '../../components/Image'

interface PostSummaryProps {
  post: WP_REST_API_Post
  className?: string
  color?: Color
  ActionButton?: JSX.Element
  addGradient?: boolean
  onClick?: () => void
}

const PostSummary = ({
    post,
    className,
    color = Color.WHITE,
    ActionButton,
    addGradient = true,
    onClick,
}: PostSummaryProps) => {
    const getImageProps = () => {
    // @ts-ignore
        if (!post._embedded['wp:featuredmedia']?.[0]?.['source_url'] && !post.acf.featured_image) {
            return undefined
        }
        // @ts-ignore
        if (typeof post?.acf?.featured_image === 'string') {
            // @ts-ignore
            if (isNaN(parseInt(post?.acf?.featured_image))) {
                // @ts-ignore
                return { src: post?.acf?.featured_image }
            } else {
                // @ts-ignore
                return { imageId: parseInt(post?.acf?.featured_image) }
            }
        } else {
            // @ts-ignore
            if (typeof post?.acf?.featured_image === 'number') {
                // @ts-ignore
                return { imageId: post?.acf?.featured_image }
            } else {
                // @ts-ignore
                return { src: post._embedded['wp:featuredmedia']?.[0]?.['source_url'] }
            }
        }
    }

    const fields = {
        title: post.title.rendered,
        content: post.excerpt?.rendered ?? post.content.rendered,
        // @ts-ignore
        image: getImageProps(),
        ...getCategoryAndTags(post),
    }

    const parseContent = (): HTMLReactParserOptions => {
        return {
            replace: (domNode) => {
                // @ts-ignore
                if ((!(domNode instanceof Element) || domNode.tagName === 'p') && domNode.children) {
                    // @ts-ignore
                    return <p className='text-sm leading-[25.2px]'>{domToReact(domNode?.children)}</p>
                }
            },
        }
    }

    const getGradientColor = () => {
        if (fields.image && color === Color.WHITE) {
            return 'from-black'
        } else if (fields.image) {
            return 'from-white'
        } else {
            return 'from-green'
        }
    }
    const renderContent = () => {
        return (
            <div
                className={twMerge(
                    'flex flex-col gap-y-3 relative h-full',
                    !fields.image && 'px-4 md:px-6 pt-16 md:pt-20',
                )}
            >
                <div className='absolute p-4 md:p-3 top-0 left-0 flex justify-between w-full items-start'>
                    <Category type={fields.category} />
                    {ActionButton && <ActionButton.type {...ActionButton.props} />}
                </div>
                {fields.image && (
                    <div className='aspect-video overflow-hidden flex-shrink-0'>
                        <Image
                            draggable={false}
                            className='object-cover object-center w-full h-full'
                            alt={fields.title}
                            {...getImageProps()}
                        />
                    </div>
                )}
                <h4 className='text-[20px] leading-[28px]'>{parse(fields.title)}</h4>
                <div className='flex-1 text-xs leading-6 overflow-hidden relative'>
                    {parse(fields.content, parseContent())}
                    {(addGradient || !fields.image) && (
                        <div
                            className={twMerge(
                                'absolute bottom-0 h-[40px] bg-gradient-to-t w-full',
                                getGradientColor(),
                            )}
                        />
                    )}
                </div>
                <div className='flex gap-1 flex-wrap'>
                    {fields.tags?.map((tag) => (
                        <Tag
                            title={tag}
                            key={`${tag}-${post.id}`}
                            color={!fields.image ? Color.BLACK : color}
                        />
                    ))}
                </div>
                <Separator
                    separatorColor={color}
                    className={twMerge('mb-0', !fields.image && 'opacity-0')}
                />
            </div>
        )
    }

    const outerClass = twMerge(
        'md:w-[30%] w-[calc(100vw-3rem)] h-auto flex-none snap-start text-white aspect-[15/18] ga-cta',
        !fields.image && 'text-black bg-green',
        color === Color.BLACK && 'text-black',
        onClick && 'cursor-pointer',
        className,
    )
    return onClick ? (
        <div onClick={onClick} className={outerClass} id={post?.slug}>
            {renderContent()}
        </div>
    ) : (
        <Link className={outerClass} to={`/resources/${fields.category}/${post.slug}`} id={post?.slug}>
            {renderContent()}
        </Link>
    )
}

export default PostSummary
